import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  isSameMonth, 
  isSameDay, 
  addDays, 
  eachDayOfInterval,
  getYear,
  setYear,
  startOfYear,
  endOfYear,
  startOfQuarter,
  endOfQuarter,
  setMonth
} from 'date-fns';
import { de } from 'date-fns/locale';

const { FiCalendar, FiChevronLeft, FiChevronRight, FiX } = FiIcons;

const CustomDatePicker = ({ 
  selectedDateRange, 
  onChange, 
  onClose,
  className = "" 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Default to current date if no range provided
  const [range, setRange] = useState(selectedDateRange || { from: new Date(), to: new Date() });
  const [viewDate, setViewDate] = useState(new Date()); // For calendar navigation
  const [activeYear, setActiveYear] = useState(getYear(new Date())); // For left panel year selection
  
  const containerRef = useRef(null);

  useEffect(() => {
    if (selectedDateRange) {
      setRange(selectedDateRange);
      if (selectedDateRange.from) {
        setViewDate(selectedDateRange.from);
        setActiveYear(getYear(selectedDateRange.from));
      }
    }
  }, [selectedDateRange]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOpen = () => setIsOpen(!isOpen);

  // --- Handlers for Left Panel Presets ---

  const handleYearChange = (increment) => {
    setActiveYear(prev => prev + increment);
  };

  const selectWholeYear = () => {
    const start = startOfYear(new Date(activeYear, 0, 1));
    const end = endOfYear(new Date(activeYear, 0, 1));
    setRange({ from: start, to: end });
    setViewDate(start);
  };

  const selectQuarter = (q) => {
    // q is 1, 2, 3, 4
    // Q1: Jan(0), Q2: Apr(3), Q3: Jul(6), Q4: Oct(9)
    const monthIndex = (q - 1) * 3;
    const dateInQuarter = new Date(activeYear, monthIndex, 1);
    const start = startOfQuarter(dateInQuarter);
    const end = endOfQuarter(dateInQuarter);
    setRange({ from: start, to: end });
    setViewDate(start);
  };

  const selectMonth = (monthIndex) => {
    const date = new Date(activeYear, monthIndex, 1);
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    setRange({ from: start, to: end });
    setViewDate(start);
  };

  // --- Handlers for Calendar ---

  const handlePrevMonth = () => setViewDate(subMonths(viewDate, 1));
  const handleNextMonth = () => setViewDate(addMonths(viewDate, 1));

  const handleDayClick = (day) => {
    // Simple logic: If no range or full range exists, start new range.
    // If 'from' exists but no 'to', set 'to'.
    // Allow single day selection if from and to are same.
    
    if (!range.from || (range.from && range.to && !isSameDay(range.from, range.to))) {
      // Start new selection
      setRange({ from: day, to: day }); // Initially single day
    } else if (range.from && (!range.to || isSameDay(range.from, range.to))) {
      // Complete the range
      if (day < range.from) {
        setRange({ from: day, to: range.from });
      } else {
        setRange({ from: range.from, to: day });
      }
    }
  };

  const applySelection = () => {
    onChange(range);
    setIsOpen(false);
  };

  const clearSelection = () => {
    const today = new Date();
    const defaultRange = { from: startOfMonth(today), to: endOfMonth(today) };
    setRange(defaultRange);
    onChange(defaultRange);
    setIsOpen(false);
  };

  // --- Render Helpers ---

  const renderCalendarDays = () => {
    const monthStart = startOfMonth(viewDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday start
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const dateFormat = "d";
    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = "";

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, dateFormat);
        const cloneDay = day;
        
        const isSelected = range.from && range.to && 
          (isSameDay(day, range.from) || isSameDay(day, range.to) || (day > range.from && day < range.to));
        const isStart = range.from && isSameDay(day, range.from);
        const isEnd = range.to && isSameDay(day, range.to);
        const isCurrentMonth = isSameMonth(day, monthStart);

        days.push(
          <div
            key={day.toString()}
            className={`
              relative w-10 h-10 flex items-center justify-center text-sm cursor-pointer rounded-full transition-all
              ${!isCurrentMonth ? "text-gray-300" : "text-gray-700"}
              ${isSelected && !isStart && !isEnd ? "bg-blue-50 text-blue-600 rounded-none" : ""}
              ${isStart ? "bg-blue-600 text-white z-10" : ""}
              ${isEnd ? "bg-blue-600 text-white z-10" : ""}
              ${isStart && isEnd ? "rounded-full" : isStart ? "rounded-l-full rounded-r-none" : isEnd ? "rounded-r-full rounded-l-none" : "hover:bg-gray-100"}
            `}
            onClick={() => handleDayClick(cloneDay)}
          >
            {formattedDate}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className="flex justify-between mb-1" key={day.toString()}>
          {days}
        </div>
      );
      days = [];
    }
    return <div className="mt-4">{rows}</div>;
  };

  const formatDateDisplay = () => {
    if (!range.from) return 'Zeitraum wählen';
    if (!range.to || isSameDay(range.from, range.to)) return format(range.from, 'dd.MM.yyyy', { locale: de });
    return `${format(range.from, 'dd.MM.yyyy', { locale: de })} - ${format(range.to, 'dd.MM.yyyy', { locale: de })}`;
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Trigger Button */}
      <button
        onClick={toggleOpen}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
      >
        <SafeIcon icon={FiCalendar} className="text-gray-500" />
        <span className="text-sm font-medium text-gray-700">{formatDateDisplay()}</span>
        <SafeIcon icon={isOpen ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} className="text-gray-400 text-xs ml-2" />
      </button>

      {/* Popover */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 flex flex-col md:flex-row overflow-hidden w-[650px] max-w-[95vw]"
          >
            {/* Left Panel: Presets */}
            <div className="w-full md:w-64 border-b md:border-b-0 md:border-r border-gray-100 p-4 bg-gray-50/50">
              {/* Year Selector */}
              <div className="flex items-center justify-between mb-4 px-2">
                <button onClick={() => handleYearChange(-1)} className="p-1 hover:bg-gray-200 rounded-full text-gray-500">
                  <SafeIcon icon={FiChevronLeft} />
                </button>
                <span className="font-bold text-gray-800 text-lg">{activeYear}</span>
                <button onClick={() => handleYearChange(1)} className="p-1 hover:bg-gray-200 rounded-full text-gray-500">
                  <SafeIcon icon={FiChevronRight} />
                </button>
              </div>

              {/* Whole Year Button */}
              <button 
                onClick={selectWholeYear}
                className="w-full py-2 mb-3 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:border-blue-500 hover:text-blue-600 transition-colors shadow-sm"
              >
                Ganzes Jahr {activeYear}
              </button>

              {/* Quarters */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                {[1, 2, 3, 4].map(q => (
                  <button
                    key={q}
                    onClick={() => selectQuarter(q)}
                    className="py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
                  >
                    Q{q} {activeYear}
                  </button>
                ))}
              </div>

              {/* Months */}
              <div className="grid grid-cols-3 gap-2">
                {['JAN', 'FEB', 'MÄR', 'APR', 'MAI', 'JUN', 'JUL', 'AUG', 'SEP', 'OKT', 'NOV', 'DEZ'].map((m, i) => (
                  <button
                    key={m}
                    onClick={() => selectMonth(i)}
                    className="py-2 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            {/* Right Panel: Calendar */}
            <div className="flex-1 p-6 bg-white">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-6">
                <button onClick={handlePrevMonth} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                  <SafeIcon icon={FiChevronLeft} />
                </button>
                <h3 className="text-base font-bold text-gray-800">
                  {format(viewDate, 'MMMM yyyy', { locale: de })}
                </h3>
                <button onClick={handleNextMonth} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                  <SafeIcon icon={FiChevronRight} />
                </button>
              </div>

              {/* Weekday Headers */}
              <div className="flex justify-between mb-2">
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
                  <div key={d} className="w-10 text-center text-xs font-medium text-gray-400">
                    {d}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              {renderCalendarDays()}

              {/* Footer Actions */}
              <div className="flex justify-between items-center mt-8 pt-4 border-t border-gray-100">
                <button 
                  onClick={clearSelection}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <SafeIcon icon={FiX} className="text-xs" /> Löschen
                </button>
                <button 
                  onClick={applySelection}
                  className="bg-gray-900 text-white text-sm font-medium px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors shadow-lg shadow-gray-200"
                >
                  Anwenden
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomDatePicker;